/**
 * Product feedback (SA-1305): the reports people send from Help & feedback, read, triaged and noted by
 * the product team. The filters and the open report ride the address with `replace`.
 */
import { useId } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MessageSquareHeart, NotebookPen, X } from 'lucide-react'
import { Badge, Button, NativeSelect, Switch } from '@/components/ui'
import { Tip } from '@/components/ui/Tip'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { RegisterTable } from '@/components/records/RegisterTable'
import { CELL, type MergedColumn } from '@/components/records/registerColumns'
import { ListPager } from '@/components/records/ListPager'
import { readUnavailable } from '@/components/records/readState'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useAdminFeedbackList } from '@/hooks/useAdminFeedback'
import type { AdminReportRow, AssessmentKind, ReportStatus } from '@/services/helpFeedback/appFeedbackApi'
import { FeedbackReportDrawer } from './FeedbackReportDrawer'
import { KindChip, StatusChip } from './feedbackChips'
import {
  KINDS, STATUSES, fullTime, groupByPage, hasFilters, readFilters, sentWords, writeFilters, type FilterPatch,
} from './feedbackAdminModel'
import * as T from './feedbackAdminText'

/** 50: the server's default page, which the request never names. */
export const PAGE_SIZE = 50

function SentCell({ iso, zone }: { iso: string; zone: string }) {
  return (
    <Tip text={fullTime(iso, zone)}>
      <span tabIndex={0} className="whitespace-nowrap">{sentWords(iso, new Date(), zone)}</span>
    </Tip>
  )
}

function columns(zone: string, onPage: (path: string) => void): MergedColumn<AdminReportRow>[] {
  const col = (id: string, header: string, render: MergedColumn<AdminReportRow>['render'], width: number,
    extra: Partial<MergedColumn<AdminReportRow>> = {}): MergedColumn<AdminReportRow> =>
    ({ id, header, headerClassName: CELL, cellClassName: CELL, render, width, ...extra })
  return [
    col('reference', T.COL_REFERENCE, (r) => <span className="font-medium">{r.reference}</span>, 96),
    col('kind', T.COL_KIND, (r) => <KindChip kind={r.kind} />, 104),
    col('summary', T.COL_SUMMARY, (r) => (
      <span className="flex items-start gap-1.5">
        <span className="line-clamp-2 min-w-0 break-words">
          {r.summary ?? <span className="text-muted-foreground">{T.NO_SUMMARY}</span>}
        </span>
        {r.help_gap && <Badge variant="warning-soft" className="shrink-0">{T.HELP_GAP}</Badge>}
        {r.has_note && (
          <Tip text={T.HAS_NOTE}>
            <span tabIndex={0} aria-label={T.HAS_NOTE} className="mt-0.5 shrink-0 text-muted-foreground">
              <NotebookPen aria-hidden="true" className="size-3.5" />
            </span>
          </Tip>
        )}
      </span>
    ), 320),
    col('page', T.COL_PAGE, (r) => r.page_path ? (
      <button type="button" aria-label={T.filterToPage(r.page_path)}
        onClick={(e) => { e.stopPropagation(); onPage(r.page_path!) }}
        className="max-w-full truncate rounded-sm font-mono text-[12px] text-foreground hover:underline">
        {r.page_path}
      </button>
    ) : <span className="text-muted-foreground">{T.DASH}</span>, 180),
    col('from', T.COL_FROM, (r) => r.sender_name ?? T.DASH, 150),
    col('sent', T.COL_SENT, (r) => <SentCell iso={r.sent_at} zone={zone} />, 140),
    col('status', T.COL_STATUS, (r) => <StatusChip status={r.status} />, 136),
  ]
}

export function FeedbackAdminPage() {
  const [params, setParams] = useSearchParams()
  const filters = readFilters(params)
  const zone = useDefaultTimezone()
  const ids = { kind: useId(), status: useId(), from: useId(), guidance: useId(), gaps: useId() }
  const list = useAdminFeedbackList({
    kind: filters.kind, status: filters.status, user: filters.user, pagePath: filters.pagePath,
    resolvedGuidance: filters.guidance, helpGap: filters.gaps, page: filters.page,
  })
  const set = (patch: FilterPatch) => setParams((was) => writeFilters(was, patch), { replace: true })
  const openReport = (id: string | null) => setParams((was) => {
    const next = new URLSearchParams(was)
    if (id) next.set('report', id)
    else next.delete('report')
    return next
  }, { replace: true })
  const data = list.data
  const filtered = hasFilters(filters)
  const clear = () => set({ kind: undefined, status: undefined, user: undefined, pagePath: undefined, guidance: false, gaps: false })

  return (
    <PageFrame width="list" className="space-y-3">
      <PageHeader title={T.TITLE} icon={MessageSquareHeart} subtitle={T.SUBTITLE} />
      <div className="flex flex-wrap items-center gap-2" data-testid="feedback-toolbar">
        <div className="w-40">
          <NativeSelect id={ids.kind} aria-label={T.KIND_FILTER} value={filters.kind ?? ''}
            onChange={(e) => set({ kind: (e.target.value || undefined) as AssessmentKind | undefined })}>
            <option value="">{T.ALL_KINDS}</option>
            {KINDS.map((k) => <option key={k} value={k}>{T.KIND_WORDS[k]}</option>)}
          </NativeSelect>
        </div>
        <div className="w-44">
          <NativeSelect id={ids.status} aria-label={T.STATUS_FILTER} value={filters.status ?? ''}
            onChange={(e) => set({ status: (e.target.value || undefined) as ReportStatus | undefined })}>
            <option value="">{T.ALL_STATUSES}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{T.STATUS_WORDS[s].label}</option>)}
          </NativeSelect>
        </div>
        <div className="w-44">
          <NativeSelect id={ids.from} aria-label={T.FROM_FILTER} value={filters.user ?? ''}
            onChange={(e) => set({ user: e.target.value || undefined })}>
            <option value="">{T.ANYONE}</option>
            {(data?.senders ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            {filters.user && !data?.senders.some((s) => s.id === filters.user) && (
              <option value={filters.user}>{filters.user}</option>
            )}
          </NativeSelect>
        </div>
        {filters.pagePath && (
          <span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium">
            <span className="font-mono">{T.pageChip(filters.pagePath)}</span>
            <Button type="button" variant="ghost" size="sm" aria-label={T.removePageChip(filters.pagePath)}
              onClick={() => set({ pagePath: undefined })} className="h-5 w-5 p-0">
              <X aria-hidden="true" className="size-3" />
            </Button>
          </span>
        )}
        <label htmlFor={ids.guidance} className="inline-flex items-center gap-2 text-sm text-foreground">
          <Switch id={ids.guidance} size="sm" checked={filters.guidance}
            onCheckedChange={(on) => set({ guidance: on })} />
          {T.guidanceToggle(data?.resolved_guidance_count ?? 0)}
        </label>
        <Tip text={T.GAPS_HINT}>
          <label htmlFor={ids.gaps} className="inline-flex items-center gap-2 text-sm text-foreground">
            <Switch id={ids.gaps} size="sm" checked={filters.gaps} onCheckedChange={(on) => set({ gaps: on })} />
            {T.GAPS_TOGGLE}
          </label>
        </Tip>
        <span className="ml-auto flex items-center gap-2">
          {data && (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {T.showing(data.items.length, data.total)}
            </span>
          )}
          {filtered && <Button type="button" variant="ghost" size="sm" onClick={clear}>{T.CLEAR_FILTERS}</Button>}
        </span>
      </div>
      {filters.gaps && data && data.items.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label={T.GAPS_TOGGLE} data-testid="feedback-gap-groups">
          {groupByPage(data.items).map((g, i) => (
            <li key={`${g.path}-${i}`}>
              {g.path ? (
                <Button type="button" variant="outline" size="sm" className="font-mono text-xs"
                  aria-label={T.filterToPage(g.path)} onClick={() => set({ pagePath: g.path! })}>
                  {T.gapGroup(g.path, g.rows.length)}
                </Button>
              ) : <span className="text-xs text-muted-foreground">{T.gapGroup(null, g.rows.length)}</span>}
            </li>
          ))}
        </ul>
      )}
      {readUnavailable(list) ? (
        <div role="alert" className="flex items-center gap-3 text-sm text-destructive">
          {T.LOAD_FAILED}
          <Button type="button" variant="outline" size="sm" onClick={() => void list.refetch()}>{T.TRY_AGAIN}</Button>
        </div>
      ) : (
        <RegisterTable<AdminReportRow> viewId="app-feedback" ariaLabel={T.TABLE_LABEL}
          columns={columns(zone, (path) => set({ pagePath: path }))}
          rows={data?.items ?? []} rowKey={(r) => r.id} rowLabel={(r) => r.reference}
          onSelect={(r) => openReport(r.id)} markedRowId={filters.report ?? null} busy={list.isFetching}
          empty={data === undefined ? T.LOADING : filtered ? T.EMPTY_FILTERED : T.EMPTY}
          footer={data ? (
            <ListPager page={filters.page} pageSize={PAGE_SIZE} total={data.total} onPage={(page) => set({ page })} />
          ) : undefined} />
      )}
      <FeedbackReportDrawer reportId={filters.report ?? null} onClose={() => openReport(null)} />
    </PageFrame>
  )
}
